import { Dispatch, ReactNode, SetStateAction } from 'react';

import lineTypeIcon from '@atom/components/common/svgIcons/lineTypeIcon.svg';
import { SearchParamType, useNavigation } from '@atom/hooks/useNavigation';
import { Icon, ListTable, Menu } from '@atom/mui';
import {
  ContentCopyIcon,
  DeleteIcon,
  EditIcon,
  LocationOffIcon,
  LocationOnIcon,
  LockIcon,
} from '@atom/mui/icons';
import { Schema } from '@atom/types/schema';
import { hasRolePermissions, ROLE_SETS } from '@atom/utilities/authUtilities';
import schemaUtilities from '@atom/utilities/schemaUtilities';
import { setDisplayDate } from '@atom/utilities/timeUtilities';

const { MenuItem } = Menu;
const {
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableFooter,
  TablePagination,
} = ListTable;

const tableFooterOffset = '3.3rem';

const styles = {
  tableContainer: {
    height: `calc(100% - ${tableFooterOffset})`,
  },
  link: {
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center',
    columnGap: '0.5rem',
  },
  optionsMenuIcon: {
    marginRight: '1rem',
  },
  iconTextContainer: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '0.5rem',
  },
};

type Props = {
  schemas: Partial<Schema>[];
  schemaCount: number;
  handleDuplicateSchema: (schemaId: string) => void;
  setSchemaToDelete: Dispatch<SetStateAction<Partial<Schema>>>;
  resultsPerPageOptions: number[];
};

const SchemaPortalTable = ({
  schemas,
  schemaCount,
  handleDuplicateSchema,
  setSchemaToDelete,
  resultsPerPageOptions,
}: Props) => {
  const [{ page, limit, sortBy, isAscending }, { Link, updateSearchParams }] =
    useNavigation({
      page: SearchParamType.NUMBER,
      limit: SearchParamType.NUMBER,
      sortBy: SearchParamType.STRING,
      isAscending: SearchParamType.STRING,
    });

  const isSortAscending = isAscending === 'asc' ? 'asc' : 'desc';

  const locationTypeDisplay = (locationType: string): ReactNode => {
    switch (locationType) {
      case 'Point':
        return (
          <div style={styles.iconTextContainer}>
            <Icon icon={LocationOnIcon} />
            Point
          </div>
        );
      case 'LineString':
        return (
          <div style={styles.iconTextContainer}>
            <img src={lineTypeIcon} />
            Line
          </div>
        );
      default:
        return (
          <div style={styles.iconTextContainer}>
            <Icon icon={LocationOffIcon} />
            No Location
          </div>
        );
    }
  };

  const handleSortChange = (sortField: string, direction: 'asc' | 'desc') =>
    updateSearchParams({ sortBy: sortField, isAscending: direction });

  return (
    <ListTable TableContainerProps={{ style: styles.tableContainer }}>
      <TableHead>
        <TableRow header>
          <TableCell
            variant="head"
            sortDirection={sortBy === 'name' ? isSortAscending : false}
            onSortChange={direction => handleSortChange('name', direction)}
          >
            Name
          </TableCell>
          <TableCell
            variant="head"
            // Note: backend sort field is `published`, FE property is `isPublished`
            sortDirection={sortBy === 'published' ? isSortAscending : false}
            onSortChange={direction => handleSortChange('published', direction)}
          >
            Status
          </TableCell>
          <TableCell
            variant="head"
            sortDirection={sortBy === 'locationType' ? isSortAscending : false}
            onSortChange={direction =>
              handleSortChange('locationType', direction)
            }
          >
            Location Type
          </TableCell>
          <TableCell
            variant="head"
            // Note: backend sort field is `material`, FE property is `isMaterial`
            sortDirection={sortBy === 'material' ? isSortAscending : false}
            onSortChange={direction => handleSortChange('material', direction)}
          >
            Cost Tracking
          </TableCell>
          <TableCell variant="head">Created By</TableCell>
          <TableCell
            variant="head"
            sortDirection={sortBy === 'createdDate' ? isSortAscending : false}
            onSortChange={direction =>
              handleSortChange('createdDate', direction)
            }
          >
            Created On
          </TableCell>
          <TableCell variant="head">Modified By</TableCell>
          <TableCell
            variant="head"
            sortDirection={sortBy === 'updatedDate' ? isSortAscending : false}
            onSortChange={direction =>
              handleSortChange('updatedDate', direction)
            }
          >
            Modified On
          </TableCell>
          <TableCell variant="head">
            {/* Blank for the additional options menu */}
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {schemas?.map(schema => (
          <TableRow key={schema.id}>
            <TableCell>
              <Link style={styles.link} to={`/inventoryTypes/${schema.id}`}>
                {schemaUtilities.getSchemaIconFromSchemaOrAsset(schema)}
                {schema.name}
              </Link>
            </TableCell>
            <TableCell>
              <div style={styles.iconTextContainer}>
                <Icon icon={schema.isPublished ? LockIcon : EditIcon} />
                {schema.isPublished ? 'Published' : 'Draft'}
              </div>
            </TableCell>
            <TableCell>{locationTypeDisplay(schema.locationType)}</TableCell>
            <TableCell>{schema.isMaterial ? 'Yes' : 'No'}</TableCell>
            <TableCell>
              {schema.createdBy?.firstName} {schema.createdBy?.lastName}
            </TableCell>
            <TableCell>{setDisplayDate(schema.createdDate)}</TableCell>
            <TableCell>
              {schema.updatedBy?.firstName} {schema.updatedBy?.lastName}
            </TableCell>
            <TableCell>{setDisplayDate(schema.updatedDate)}</TableCell>
            <TableCell>
              {(schema.isPublished || hasRolePermissions(ROLE_SETS.ADMIN)) && (
                <Menu IconButtonProps={{ style: styles.optionsMenuIcon }}>
                  {schema.isPublished && (
                    <MenuItem
                      startAdornment={<Icon icon={ContentCopyIcon} />}
                      onClick={() => handleDuplicateSchema(schema.id)}
                    >
                      Duplicate
                    </MenuItem>
                  )}
                  {hasRolePermissions(ROLE_SETS.ADMIN) && (
                    <MenuItem
                      startAdornment={<Icon icon={DeleteIcon} />}
                      onClick={() => setSchemaToDelete(schema)}
                    >
                      Delete
                    </MenuItem>
                  )}
                </Menu>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
      <TableFooter>
        <TableRow>
          <TablePagination
            rowsPerPageOptions={resultsPerPageOptions}
            count={schemaCount}
            rowsPerPage={limit}
            page={page}
            onPageChange={nextPage => updateSearchParams({ page: nextPage })}
            onRowsPerPageChange={event =>
              updateSearchParams({ limit: +event.target.value, page: 1 })
            }
          />
        </TableRow>
      </TableFooter>
    </ListTable>
  );
};

export default SchemaPortalTable;
